// ./src/components/features/EventVendors/VendorDocumentsList/VendorDocumentsList.tsx
//
// ROADMAP #170 — the documents a vendor holds: a table on desktop, cards below (buttons on a tablet, a swipe on a phone),
// like every list (owner, 2026-10-20: "on large screens we use tables and on small screens cards"). Each row: what it is
// and its number, when it is valid, its files, and who put it on file. Used on Library › Vendors (with Correct / Remove)
// and on an event's vendor page as "On file" (read only — there is no handler to offer).
// ⓘ Library › Vendors pages it (Load more — `useVendorDocuments`); the event's "On file" passes the event vendor's current
//   documents whole (no `onLoadMore`) — a vendor has only a few still good for one event.
import React, { useCallback, useMemo } from "react";
import { Paper, Stack, Group, Text, Box, ActionIcon, Tooltip, Skeleton, Table } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { VendorDocument } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";
import {
  AppIcon,
  EmptyState,
  LoadMoreButton,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
} from "@/components/generic";

import { DocumentFiles, DocumentNumber, DocumentSource, DocumentValidity } from "../VendorDocumentParts/VendorDocumentParts";

export interface VendorDocumentsListProps {
  data: VendorDocument[];
  /** ⚠ REQUIRED — whether the FIRST read has come back (the skeleton belongs to it alone). ▶ check-list-states */
  isLoaded: boolean;
  /** True when a search is narrowing the list — then an empty one is "no match", not "none yet". */
  hasQueryNarrowing?: boolean;
  /** Correct what was read off it. Absent = read only. */
  onEdit?: (doc: VendorDocument) => void;
  /** Off the vendor's file. Absent = read only. */
  onDelete?: (doc: VendorDocument) => void;
  isRemoving: (id?: string) => boolean;
  highlightedId?: string | null;
  emptyTitle: string;
  emptyBody: string;
  /** First-run empty state's action. */
  onAdd?: () => void;
  addLabel?: string;
  /** Server paging — a Load more button when given. */
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  loadingMore?: boolean;
}

interface RowProps {
  doc: VendorDocument;
  onEdit?: (doc: VendorDocument) => void;
  onDelete?: (doc: VendorDocument) => void;
  highlighted: boolean;
  isDeleting: boolean;
}

const NameCell: React.FC<{ doc: VendorDocument }> = ({ doc }) => (
  <Stack gap={2} style={{ minWidth: 0 }}>
    <Group gap={6} wrap="nowrap">
      <AppIcon iconName="FileCertificate" size={18} />
      <Text size="md" fw={600} truncate>
        {doc.name}
      </Text>
    </Group>
    <DocumentNumber document={doc} />
    {doc.notes ? (
      <Text size="md" c="dimmed" lineClamp={2}>
        {doc.notes}
      </Text>
    ) : null}
  </Stack>
);

const RowActions: React.FC<RowProps> = ({ doc, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["vendors", "common"]);

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      {onEdit && recordHasRight(doc, "edit") ? (
        <Tooltip label={t("vendors:document.correct", { defaultValue: "Correct" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="neutral"
            size="lg"
            disabled={isDeleting}
            onClick={() => onEdit(doc)}
            aria-label={t("vendors:document.correct", { defaultValue: "Correct" })}
          >
            <AppIcon iconName="Pencil" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onDelete && recordHasRight(doc, "remove") ? (
        <Tooltip label={t("vendors:document.remove", { defaultValue: "Remove from the vendor's file" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="error"
            size="lg"
            loading={isDeleting}
            onClick={() => onDelete(doc)}
            aria-label={t("vendors:document.remove", { defaultValue: "Remove from the vendor's file" })}
          >
            <AppIcon iconName="Trash" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
};

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const DocumentCardComponent: React.FC<CardProps> = (p) => {
  const { t } = useTranslation(["vendors", "common"]);
  const { doc } = p;
  const hasActions = (!!p.onEdit && recordHasRight(doc, "edit")) || (!!p.onDelete && recordHasRight(doc, "remove"));

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (p.onEdit && recordHasRight(doc, "edit")) {
      actions.push({
        key: "edit",
        icon: "Pencil",
        label: t("vendors:document.correct", { defaultValue: "Correct" }),
        color: "blue",
        onClick: () => p.onEdit?.(doc),
        disabled: p.isDeleting,
      });
    }

    if (p.onDelete && recordHasRight(doc, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("vendors:document.removeShort", { defaultValue: "Remove" }),
        color: "red",
        onClick: () => p.onDelete?.(doc),
        loading: p.isDeleting,
        disabled: p.isDeleting,
      });
    }

    return actions;
  }, [p, doc, t]);

  const cardBody = (showActions: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={p.isDeleting ? 0.5 : 1}
      style={{ transition: "opacity 150ms ease", ...(p.highlighted ? HIGHLIGHT_STYLE : {}) }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
          <NameCell doc={doc} />
          <DocumentValidity document={doc} />
          <DocumentFiles document={doc} />
          <DocumentSource document={doc} />
        </Stack>
        {showActions && hasActions ? <RowActions {...p} /> : null}
      </Group>
    </Paper>
  );

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={p.forceClose}
            onForceCloseComplete={p.onForceCloseComplete}
            onSwipeStateChange={p.onSwipeStateChange}
            disabled={p.isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false)}
          </SwipeableCard>
        ) : (
          cardBody(false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true)}</Box>
    </>
  );
};

const DocumentCard = React.memo(DocumentCardComponent);

const VendorDocumentsListComponent: React.FC<VendorDocumentsListProps> = ({
  data,
  isLoaded,
  hasQueryNarrowing = false,
  onEdit,
  onDelete,
  isRemoving,
  highlightedId,
  emptyTitle,
  emptyBody,
  onAdd,
  addLabel,
  onLoadMore,
  hasMore = false,
  loading = false,
  loadingMore = false,
}) => {
  const { t } = useTranslation(["vendors", "common"]);
  const { isDesktop } = useAppEnvironment();
  // The tablet card's row buttons (Correct + Remove) — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEdit, onDelete);
  const withActions = cardActions > 0;
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleEdit = useCallback(
    (doc: VendorDocument) => {
      closeAllCards();
      onEdit?.(doc);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (doc: VendorDocument) => {
      closeAllCards();
      onDelete?.(doc);
    },
    [onDelete, closeAllCards]
  );

  const header = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("vendors:document.columns.document", { defaultValue: "Document" })}</Table.Th>
        <Table.Th style={{ width: 240 }}>{t("vendors:document.columns.valid", { defaultValue: "Valid" })}</Table.Th>
        <Table.Th style={{ width: 280 }}>{t("vendors:document.columns.files", { defaultValue: "Files" })}</Table.Th>
        <Table.Th style={{ width: 180 }}>{t("vendors:document.columns.source", { defaultValue: "Added by" })}</Table.Th>
        {withActions ? <Table.Th style={{ width: 100 }} /> : null}
      </Table.Tr>
    </Table.Thead>
  );

  // ⚠ The FIRST read only. ▶ npm run check-list-states
  if (!isLoaded) {
    return (
      <Stack gap="xs" aria-busy="true">
        <Box visibleFrom="md">
          <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              {header}
              <Table.Tbody>
                {[0, 1, 2].map((i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Skeleton height={16} width="60%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width="70%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width="80%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width="70%" />
                    </Table.Td>
                    {withActions ? (
                      <Table.Td>
                        <Group gap={4} wrap="nowrap" justify="flex-end">
                          {Array.from({ length: cardActions }).map((_, a) => (
                            <Skeleton key={a} circle height={34} width={34} />
                          ))}
                        </Group>
                      </Table.Td>
                    ) : null}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Box>
        {[0, 1, 2].map((i) => (
          // The phone card: the name, when it is valid, a file, who added it.
          <Paper key={`p-${i}`} withBorder radius="md" p="md" hiddenFrom="sm">
            <CardSkeletonLines count={4} gap={6} />
          </Paper>
        ))}
        {[0, 1, 2].map((i) => (
          <Paper key={`t-${i}`} withBorder radius="md" p="md" visibleFrom="sm" hiddenFrom="md">
            <Group gap="md" wrap="nowrap" justify="space-between" align="flex-start">
              <CardSkeletonLines count={4} gap={6} />
              <CardSkeletonActions count={cardActions} />
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  if (data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("vendors:documents.noMatch", { defaultValue: "No documents match your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={emptyTitle}
          description={emptyBody}
          iconName="FileCertificate"
          actionLabel={onAdd ? addLabel : undefined}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  const rowProps = (doc: VendorDocument): RowProps => ({
    doc,
    onEdit: onEdit ? handleEdit : undefined,
    onDelete: onDelete ? handleDelete : undefined,
    highlighted: doc.id === highlightedId,
    isDeleting: isRemoving(doc.id),
  });

  const loadMore = onLoadMore ? <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} /> : null;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            {header}
            <Table.Tbody>
              {data.map((doc) => {
                const p = rowProps(doc);

                return (
                  <Table.Tr
                    key={doc.id}
                    style={{
                      verticalAlign: "top",
                      opacity: p.isDeleting ? 0.5 : 1,
                      backgroundColor: p.highlighted ? HIGHLIGHT_ROW_BG : undefined,
                      transition: "background-color 150ms ease, opacity 150ms ease",
                    }}
                  >
                    <Table.Td>
                      <NameCell doc={doc} />
                    </Table.Td>
                    <Table.Td>
                      <DocumentValidity document={doc} />
                    </Table.Td>
                    <Table.Td>
                      <DocumentFiles document={doc} />
                    </Table.Td>
                    <Table.Td>
                      <DocumentSource document={doc} />
                    </Table.Td>
                    {withActions ? (
                      <Table.Td>
                        <RowActions {...p} />
                      </Table.Td>
                    ) : null}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
        {loadMore}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((doc) => (
          <DocumentCard
            key={doc.id}
            {...rowProps(doc)}
            forceClose={shouldForceClose(doc.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(doc.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(doc.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const VendorDocumentsList = React.memo(VendorDocumentsListComponent);
